import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';

import {
  getCollection,
  find as apiFindRecord,
  createOrUpdate as apiCreateOrUpdate,
  deleteRecord as apiDeleteRecord,
} from 'api/market-view-api';
import type { GetCollectionOptions } from 'api/market-view-api';
import type {
  FilterValue,
  SortDirection,
} from 'helpers/api/build-query-params';

import { transformRecordFromServer } from 'helpers/server-data-transformers';

import toast from 'components/partials/toast/toast';

export interface ClientRecord {
  id: string | number;
  name?: string;
  subscriptionStatus?: string;
  [key: string]: unknown;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  pageCount: number;
  totalRecords: number;
}

interface CollectionLoadResponse {
  data: ClientRecord[];
  pagination: PaginationInfo;
}

export const clientsKeys = {
  all: ['clients'] as const,
  lists: () => [...clientsKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) =>
    [...clientsKeys.lists(), params] as const,
  details: () => [...clientsKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...clientsKeys.details(), id] as const,
};

const transformClient = (data: unknown): ClientRecord =>
  transformRecordFromServer('client', data) as ClientRecord;

// ─── List query (paginated, used by clients-admin-page) ─────────────────────

export interface UseClientsListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
  searchTerms?: string;
  enabled?: boolean;
}

export interface UseClientsListResult {
  data: ClientRecord[];
  pagination: PaginationInfo | null;
  loading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

export const useClientsList = (
  params: UseClientsListParams = {}
): UseClientsListResult => {
  const {
    pageSize = 10,
    pageIndex = 0,
    sortBy = [],
    filters = {},
    searchTerms = '',
    enabled = true,
  } = params;

  const queryKey = clientsKeys.list({
    pageSize,
    pageIndex,
    sortBy,
    filters,
    searchTerms,
  });

  const query: UseQueryResult<CollectionLoadResponse, Error> = useQuery({
    queryKey,
    queryFn: async () => {
      const options: GetCollectionOptions = {
        pageSize,
        pageIndex,
        sortBy,
        filters,
        searchTerms,
        forceSearch: false,
      };

      const response = await getCollection('clients', options);
      const records = response.data as unknown[];

      const transformed = Array.isArray(records)
        ? records.map(transformClient)
        : [];

      return { data: transformed, pagination: response.pagination };
    },
    enabled,
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data?.data ?? [],
    pagination: query.data?.pagination ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

// ─── Single client query (replaces find + useCurrentClient) ─────────────────

export interface UseClientResult {
  data: ClientRecord | null;
  loading: boolean;
  error: unknown;
}

export const useClient = (
  clientId: string | number | null | undefined
): UseClientResult => {
  const query: UseQueryResult<ClientRecord, Error> = useQuery({
    queryKey: clientsKeys.detail(clientId ?? ''),
    queryFn: async () => {
      const response = await apiFindRecord('clients', clientId!);

      return transformClient(response.data);
    },
    enabled: !!clientId,
  });

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error,
  };
};

// ─── Imperative find (for currentUser.thunk.ts dependency) ──────────────────

export const findClient = async (
  id: string | number
): Promise<{ data: ClientRecord }> => {
  const response = await apiFindRecord('clients', id);

  return { data: transformClient(response.data) };
};

// ─── All clients (for user-details-form select, no pagination) ──────────────

export interface UseAllClientsResult {
  data: ClientRecord[];
  loading: boolean;
  error: unknown;
}

export const useAllClients = (): UseAllClientsResult => {
  const query: UseQueryResult<ClientRecord[], Error> = useQuery({
    queryKey: clientsKeys.list({ all: true }),
    queryFn: async () => {
      const response = await getCollection('clients', {
        pageSize: 9999,
        pageIndex: 0,
        sortBy: [],
        filters: {},
      });

      const records = response.data as unknown[];

      return Array.isArray(records) ? records.map(transformClient) : [];
    },
  });

  return {
    data: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
  };
};

// ─── Mutations ──────────────────────────────────────────────────────────────

export const useCreateOrUpdateClient = (): UseMutationResult<
  { data: ClientRecord },
  Error,
  { id?: string | number; name?: string } & Record<string, unknown>
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiCreateOrUpdate('clients', data);

      return { data: transformClient(response.data) };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
      const action = variables.id ? 'updated' : 'created';

      toast.success({
        title: `Client account ${action}`,
        message: variables.id
          ? `You've successfully updated ${variables.name}`
          : `You've successfully added ${variables.name} as a client`,
      });
    },
    onError: (error) => {
      toast.error({
        title: 'Error creating or updating client',
        message: (error as { data?: { detail?: string } })?.data?.detail,
      });
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string | number; name?: string; silent?: boolean }
  >({
    mutationFn: async ({ id }) => {
      await apiDeleteRecord('clients', id);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
      if (!variables.silent) {
        toast.info({
          title: 'Client deleted',
          message: `You've successfully deleted this client`,
        });
      }
    },
    onError: (error) => {
      const status = (error as { status?: number })?.status;

      if (status === 405) {
        toast.error({
          title: 'Client not deleted',
          message: 'Cannot delete a client with users',
        });
      } else {
        toast.error({
          title: 'Error deleting client',
          message: 'An error occurred when deleting this client.',
        });
      }
    },
  });
};

export const useLockClient = (): UseMutationResult<
  { data: ClientRecord },
  Error,
  ClientRecord
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (client) => {
      const isLocked = client.subscriptionStatus === 'LOCKED';
      const updated = {
        ...client,
        subscriptionStatus: isLocked ? 'ACTIVE' : 'LOCKED',
      };
      const response = await apiCreateOrUpdate('clients', updated);

      return { data: transformClient(response.data) };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
      const changeMsg =
        result.data.subscriptionStatus === 'LOCKED' ? 'locked' : 'unlocked';
      const changeColor =
        result.data.subscriptionStatus === 'LOCKED' ? 'info' : 'success';

      (
        toast as unknown as Record<
          string,
          (args: { title: string; message: string }) => void
        >
      )[changeColor]({
        title: `Client ${changeMsg}`,
        message: `You've successfully ${changeMsg} ${result.data.name}`,
      });
    },
  });
};
